import React, { useState } from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';

// Status → badge text/colour, soft tone, and the darker left-accent bar.
const STATUS_BADGE = {
  unassigned: { t: 'Unassigned', c: 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300' },
  scheduled: { t: 'Assigned', c: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
  completed: { t: 'Done', c: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400' },
  in_progress: { t: 'Live now', c: 'bg-sky-100 text-sky-600 dark:bg-sky-900/20 dark:text-sky-300' },
  missed: { t: 'Missed', c: 'bg-rose-100 text-rose-600 dark:bg-rose-900/20 dark:text-rose-300' },
};
const CARD_TONE = {
  unassigned: 'border-purple-200 bg-purple-50/70 dark:border-purple-900/50 dark:bg-purple-900/20',
  scheduled: 'border-blue-200 bg-blue-50/40 dark:border-blue-900/50 dark:bg-blue-900/20',
  completed: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-900/10',
  in_progress: 'border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-900/20',
  missed: 'border-rose-200 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-900/20',
};
const CARD_ACCENT = {
  unassigned: '#a855f7', scheduled: '#2563eb', completed: '#10b981', in_progress: '#0ea5e9', missed: '#ef4444',
};

// Rich visit card: package title + status badge, time, and the related name —
// with a darker coloured left accent bar. Unassigned cards are draggable onto a
// carer row below.
const VisitChip = ({ visit, onOpen, secondary, draggable, onDragStart, onDragEnd }) => {
  const b = STATUS_BADGE[visit.status] || STATUS_BADGE.scheduled;
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!!draggable}
      onDragStart={draggable ? (e) => onDragStart(e, visit) : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      onClick={() => onOpen(visit)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(visit); } }}
      style={{ borderLeftColor: CARD_ACCENT[visit.status] || CARD_ACCENT.scheduled }}
      className={`min-w-[190px] max-w-[230px] overflow-hidden rounded-xl border border-l-4 px-3 py-2 text-left transition hover:brightness-95 ${CARD_TONE[visit.status] || CARD_TONE.scheduled} ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${visit.__draft ? 'ring-2 ring-amber-400' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-[12px] font-bold text-gray-800 dark:text-gray-200">{visit.package_name}</span>
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${b.c}`}>{b.t}</span>
      </div>
      <div className="mt-1 text-[11px] font-semibold text-gray-600 dark:text-gray-400">{visit.start}{visit.end ? `–${visit.end}` : ''}</div>
      <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">{secondary}</div>
    </div>
  );
};

const CarerRow = ({ carer, onOpen, dropActive, onDropVisit, onEditAvailability }) => {
  const [hover, setHover] = useState(false);
  const dropProps = dropActive ? {
    onDragEnter: (e) => { e.preventDefault(); setHover(true); },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHover(false); },
    onDrop: (e) => { e.preventDefault(); setHover(false); onDropVisit(e, carer.id); },
  } : {};
  return (
    <div
      {...dropProps}
      className={`grid grid-cols-1 items-center gap-3 border-t px-4 py-3 transition md:grid-cols-[200px_1fr_90px] ${hover ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/10' : 'border-gray-50 dark:border-gray-800'}`}
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-blue-100 dark:bg-blue-900/20 text-[11px] font-bold text-blue-700 dark:text-blue-400">{carer.initials}</span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{carer.name}</div>
          <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">Covers {carer.covers}</div>
          {/* Shift window for the day. The TIME itself is the trigger: click it
              to open the availability modal (to increase their availability).
              Carers with no shift this day just show a plain, non-clickable note. */}
          {carer.shift ? (
            <button
              type="button"
              onClick={() => onEditAvailability && onEditAvailability(carer)}
              title="Click the shift time to add / increase availability"
              className="truncate text-left text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              {carer.shift.start}–{carer.shift.end}
            </button>
          ) : (
            <div className="truncate text-[10px] text-gray-400 dark:text-gray-500">No shift</div>
          )}
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {carer.visits.length === 0
          ? <span className="text-[11px] text-gray-400 dark:text-gray-500">{dropActive ? 'Drop a visit here to assign' : 'No visits assigned'}</span>
          : carer.visits.map((v) => <VisitChip key={`${v.assignment_id}-${v.start}`} visit={{ ...v, _carerId: carer.id }} onOpen={onOpen} secondary={v.client?.name} />)}
      </div>
      <div className="text-right text-[11px] text-gray-400 dark:text-gray-500"><b className="block text-base text-gray-700 dark:text-gray-200">{carer.visit_count}</b> visits today</div>
    </div>
  );
};

const ZoneCard = ({ zone, onOpen, onAssign, onEditAvailability }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const canDrop = !!onAssign;

  const onDragStart = (e, visit) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ assignment_id: visit.assignment_id, zoneId: zone.id ?? null }));
    setDragging(true);
  };
  const handleDrop = (e, carerId) => {
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch (_) { return; }
    setDragging(false);
    if (!payload || payload.assignment_id == null) return;
    if ((payload.zoneId ?? null) !== (zone.id ?? null)) return; // same zone only
    onAssign(payload.assignment_id, carerId);
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] shadow-sm">
      <header className="flex items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCollapsed((c) => !c)} className="grid h-7 w-7 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
            <ChevronDown className={`h-4 w-4 transition ${collapsed ? '-rotate-90' : ''}`} />
          </button>
          <span className="h-3 w-3 rounded-full" style={{ background: zone.color || '#2563eb' }} />
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{zone.name}</div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500">{zone.postcode_prefixes?.length ? `Travel area · ${zone.postcode_prefixes.join(', ')}` : 'No travel area'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold">
          <span className="rounded-md bg-gray-100 dark:bg-[#31373f] px-2 py-1 text-gray-500 dark:text-gray-300">{zone.carers.length} carers cover zone</span>
          {zone.unassigned.length > 0 && <span className="rounded-md bg-purple-50 dark:bg-purple-900/20 px-2 py-1 text-purple-600 dark:text-purple-300">{zone.unassigned.length} unassigned</span>}
        </div>
      </header>
      {!collapsed && (
        <div>
          {/* Unassigned FIRST — drag a card onto a carer row below to assign */}
          {zone.unassigned.length > 0 && (
            <div className="grid grid-cols-1 items-center gap-3 border-b border-gray-100 dark:border-gray-800 bg-purple-50/40 dark:bg-purple-900/10 px-4 py-3 md:grid-cols-[200px_1fr_90px]">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-500 dark:text-purple-300"><AlertCircle className="h-4 w-4" /></span>
                <div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">Unassigned visits</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500">{canDrop ? 'Drag onto a carer below' : 'Action required'}</div>
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {zone.unassigned.map((v) => (
                  <VisitChip
                    key={`${v.assignment_id}-${v.start}`}
                    visit={v}
                    onOpen={onOpen}
                    secondary={v.client?.name || 'Unassigned'}
                    draggable={canDrop}
                    onDragStart={onDragStart}
                    onDragEnd={() => setDragging(false)}
                  />
                ))}
              </div>
              <div className="text-right text-[11px] text-gray-400 dark:text-gray-500"><b className="block text-base text-gray-700 dark:text-gray-200">{zone.unassigned.length}</b> open</div>
            </div>
          )}

          {zone.carers.map((c) => (
            <CarerRow
              key={c.id}
              carer={c}
              onOpen={onOpen}
              dropActive={canDrop && dragging}
              onDropVisit={handleDrop}
              onEditAvailability={onEditAvailability}
            />
          ))}
          {zone.carers.length === 0 && zone.unassigned.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No carers cover this zone yet.</div>
          )}
        </div>
      )}
    </section>
  );
};

const PackageCarerView = ({ zones = [], onOpen, onAssign, onEditAvailability }) => {
  if (zones.length === 0) {
    return <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-[#262b33] px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">No care packages for this day.</div>;
  }
  return (
    <div className="flex flex-col gap-4">
      {zones.map((z) => <ZoneCard key={z.id ?? 'unzoned'} zone={z} onOpen={onOpen} onAssign={onAssign} onEditAvailability={onEditAvailability} />)}
    </div>
  );
};

export default PackageCarerView;
